import {
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect } from 'react'
import { MapContainer, Marker, Polygon, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import 'leaflet/dist/leaflet.css'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { useMapImage } from '~/map/use-map-image'
import { useGlobalClimate } from '~/farm/add-farm/use-global-climate'
import { getWrappedBoundary } from '~/farm/add-farm/coordinate-utils'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const DEFAULT_ZOOM = 7.75
const HEX_COUNT = 7

export const FarmMapsCard = ({ climate }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { climateHexes, fetchNearest } = useGlobalClimate()
  const { mapUrl, loading: mapLoading } = useMapImage(climate)

  useEffect(() => {
    if (climate?.lat && climate?.lng) {
      fetchNearest(climate.lat, climate.lng, HEX_COUNT)
    }
  }, [climate?.lat, climate?.lng, fetchNearest])

  return (
    <Card variant="outlined">
      <CardHeader title="Maps" sx={{ pb: 0 }} />
      <CardContent>
        <Stack
          direction={isDesktop ? 'row' : 'column'}
          spacing={2}
        >
          {/* Left: Static farm property map */}
          <Box
            sx={{
              flex: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              aspectRatio: '1 / 1',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, pt: 0.5, flexShrink: 0 }}>
              Farm property
            </Typography>
            <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
              {mapLoading ? (
                <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
              ) : mapUrl ? (
                <Box
                  component="img"
                  src={mapUrl}
                  alt="Farm property map"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No map image available</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right: OSM climate hex map */}
          <Box
            sx={{
              flex: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              aspectRatio: '16 / 9',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, pt: 0.5, flexShrink: 0 }}>
              Climate zone
            </Typography>
            {climate?.lat && climate?.lng ? (
              <MapContainer
                center={[climate.lat, climate.lng]}
                zoom={DEFAULT_ZOOM}
                zoomSnap={0.25}
                zoomDelta={0.25}
                zoomControl={false}
                style={{ flex: 1, minHeight: 0 }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
                <Marker position={[climate.lat, climate.lng]} alt="" />
                {climateHexes.map((hex) => (
                  <Polygon
                    key={hex.h3_index}
                    positions={getWrappedBoundary(hex.h3_index)}
                    pathOptions={{
                      fillColor: '#3b82f6',
                      fillOpacity: 0.3,
                      weight: 1,
                    }}
                  />
                ))}
              </MapContainer>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No location data</Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
